/**
 * Verse parser — single source of truth.
 *
 * Used by the internal /tools/verse-json page to split pasted text into a
 * single-language array of verses for the new
 *
 *   transliteration: { en: [], te: [], hi: [] }
 *   meaning:         { en: [], te: [], hi: [] }
 *
 * data model.
 *
 * Splitting rules (in order of priority):
 *
 *   Transliteration:
 *     1. `|| N ||` markers where N is a Latin (0-9), Devanagari (०-९) or
 *        Telugu (౦-౯) digit sequence. The marker is kept with the preceding
 *        verse so the rendered output still shows it.
 *     2. If no marker is found, fall back to splitting on blank lines
 *        (one or more newlines containing only whitespace).
 *
 *   Meaning:
 *     1. Lines starting with `N.` or `N)` where N is Latin / Devanagari /
 *        Telugu digits.
 *     2. Fallback to blank-line splitting.
 *
 *   If parsing still yields nothing, return [] (caller renders a warning).
 */

// Latin 0-9, Devanagari ०-९ (U+0966–U+096F), Telugu ౦-౯ (U+0C66–U+0C6F).
const DIGIT_CLASS = "[0-9\\u0966-\\u096F\\u0C66-\\u0C6F]+";

const VERSE_MARKER_RE = new RegExp(`\\|\\|\\s*${DIGIT_CLASS}\\s*\\|\\|`, "g");
const MEANING_MARKER_RE = new RegExp(
  `(?:^|\\n)\\s*${DIGIT_CLASS}\\s*[.)]\\s*`,
  "g",
);
const BLANK_LINE_RE = /\n\s*\n+/g;

const cleanList = (arr) =>
  arr
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter((s) => s.length > 0);

export const splitTransliteration = (text) => {
  if (!text || !text.trim()) return [];

  // Pass 1 — verse markers, keeping the marker with the preceding verse.
  VERSE_MARKER_RE.lastIndex = 0;
  const matches = [];
  let m;
  while ((m = VERSE_MARKER_RE.exec(text)) !== null) matches.push(m);

  if (matches.length > 0) {
    const pieces = [];
    let cursor = 0;
    for (const mm of matches) {
      const end = mm.index + mm[0].length;
      pieces.push(text.slice(cursor, end));
      cursor = end;
    }
    const tail = text.slice(cursor);
    if (tail.trim()) pieces.push(tail);
    return cleanList(pieces);
  }

  // Pass 2 — blank-line fallback.
  return cleanList(text.split(BLANK_LINE_RE));
};

export const splitMeaning = (text) => {
  if (!text || !text.trim()) return [];

  // Pass 1 — numbered markers at start of line.
  MEANING_MARKER_RE.lastIndex = 0;
  const matches = [];
  let m;
  while ((m = MEANING_MARKER_RE.exec(text)) !== null) matches.push(m);

  if (matches.length > 0) {
    const pieces = matches.map((mm, i) => {
      const start = mm.index + mm[0].length;
      const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
      return text.slice(start, end);
    });
    return cleanList(pieces);
  }

  // Pass 2 — blank-line fallback.
  return cleanList(text.split(BLANK_LINE_RE));
};

/** Heuristic dispatcher used by the /tools/verse-json page. */
export const splitContent = (text, contentType) =>
  contentType === "meaning" ? splitMeaning(text) : splitTransliteration(text);

/** Pretty-print a single language block (the only shape the tool outputs). */
export const formatLanguageBlock = (langCode, verses) => {
  // Keep quotes & escapes JSON-safe; indent uses 2 spaces.
  const inner = verses.map((v) => "  " + JSON.stringify(v)).join(",\n");
  return `"${langCode}": [\n${inner}\n]`;
};
